// Feature #309 — the "Technical skills" block on My Skills and Staff Detail.
//
// A strength spine, not a radar: the technical axis set is self-selected
// out of a 100+ entry catalogue, so it would silently redraw itself
// between visits and break at zero skills. An ordered list answers the
// only real question — how deep, and where — and looks deliberate at 0, 6
// and 60 skills.
//
// Five discrete pips, never a continuous bar. The rubric has five named
// rungs and no half-steps; a bar invites "3.5" and invites averaging.
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDateTime } from '@/utils/dateFormat'
import { SmProfileSkill } from '@/api/sm'
import {
  StaleBadge,
  blockStyle,
  kpiLabel,
  kpiValue,
  toggleButton,
  toggleGroup,
  useRubric,
} from './SmShared'

interface StrengthSpineProps {
  skills: SmProfileSkill[]
  /** Subject of the profile — the "start an assessment" CTA target. */
  userId: string
}

type SpineSort = 'rating' | 'name' | 'assessed'

const SORTS: { key: SpineSort; label: string }[] = [
  { key: 'rating', label: 'By rating' },
  { key: 'name', label: 'A–Z' },
  { key: 'assessed', label: 'Recently assessed' },
]

const TOP_N = 8
const MAX_LEVEL = 5

/**
 * `source_label` already ends with its own date ("… on 07 Jul 2026"), so
 * pairing it with `last_assessed_at` would print two dates. The backend
 * emits a date-free `source_actor_label` for exactly this row. The strip
 * below is only a fallback for a stale cached response.
 */
const actorLabel = (skill: SmProfileSkill): string => {
  if (skill.source_actor_label) return skill.source_actor_label
  if (skill.source_label) {
    return skill.source_label.replace(/\s+on\s+\d{1,2}\s+\S+\s+\d{4}$/, '').trim() || '—'
  }
  return '—'
}

export const StrengthSpine: React.FC<StrengthSpineProps> = ({ skills, userId }) => {
  const [sort, setSort] = useState<SpineSort>('rating')
  const [showAll, setShowAll] = useState(false)
  const rubric = useRubric()

  const technicalLevels = useMemo(
    () =>
      rubric
        .filter((r) => r.skill_type === 'technical')
        .slice()
        .sort((a, b) => a.value - b.value),
    [rubric]
  )

  const technical = useMemo(
    () => skills.filter((s) => s.skill_type === 'technical'),
    [skills]
  )

  /**
   * Feature #312 — the rubric definition for a rating, or undefined when
   * there should be no tooltip at all. Unrated skills and a rubric that has
   * not resolved yet both fall through to undefined rather than rendering an
   * empty tooltip; a blanked description (the field is sm_admin-editable and
   * nullable) falls back to the short form.
   *
   * This reads the *technical* rubric, which since #355 is the only set the
   * API serves. The `skill_type` filter is kept: it is still the correct
   * question, and `sm_rubric_level` still holds a second set.
   */
  const levelDefinition = (rating?: number | null): string | undefined => {
    if (rating == null) return undefined
    const level = technicalLevels.find((r) => r.value === rating)
    if (!level) return undefined
    const description = level.description?.trim()
    return description || `${level.value} · ${level.label}`
  }

  const sorted = useMemo(() => {
    const list = technical.slice()
    if (sort === 'name') {
      list.sort((a, b) => a.skill_name.localeCompare(b.skill_name))
    } else if (sort === 'assessed') {
      list.sort(
        (a, b) =>
          (b.last_assessed_at || '').localeCompare(a.last_assessed_at || '') ||
          (b.current_rating ?? 0) - (a.current_rating ?? 0)
      )
    } else {
      list.sort(
        (a, b) =>
          (b.current_rating ?? 0) - (a.current_rating ?? 0) ||
          a.skill_name.localeCompare(b.skill_name)
      )
    }
    return list
  }, [technical, sort])

  if (technical.length === 0) {
    return (
      <section style={blockStyle}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
          Technical skills
        </h2>
        <div
          style={{
            border: '1px dashed var(--color-light-grey)',
            borderRadius: '8px',
            padding: '28px 20px',
            textAlign: 'center',
          }}
        >
          <p style={{ color: 'var(--color-dark-grey)', marginBottom: '14px' }}>
            No technical skills claimed yet.
          </p>
          <Link
            to={`/sm/assess/${userId}`}
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Start your first assessment
          </Link>
        </div>
      </section>
    )
  }

  const shown = showAll ? sorted : sorted.slice(0, TOP_N)
  const independent = technical.filter((s) => (s.current_rating ?? 0) >= 3).length
  const stale = technical.filter((s) => s.is_stale).length

  return (
    <section style={blockStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Technical skills</h2>
        <div style={toggleGroup} role="group" aria-label="Sort technical skills">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-pressed={sort === s.key}
              onClick={() => setSort(s.key)}
              style={toggleButton(sort === s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: '4px 0 18px' }}>
        Five pips are the five rubric levels — there is no half-step.
      </p>

      <div style={{ display: 'flex', gap: '26px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <div style={kpiValue}>{technical.length}</div>
          <div style={kpiLabel}>Skills on profile</div>
        </div>
        <div>
          <div style={kpiValue}>{independent}</div>
          <div style={kpiLabel}>Level 3+ (independent)</div>
        </div>
        <div>
          <div style={kpiValue}>{stale}</div>
          <div style={kpiLabel}>Stale</div>
        </div>
      </div>

      <div>
        {shown.map((s) => {
          const level = s.current_rating ?? 0
          const subtext = `${actorLabel(s)} • ${formatDateTime(s.last_assessed_at)}`
          return (
            <div
              key={s.skill_id}
              className="sm-spine-row"
              title={`${s.skill_name}\nLevel ${s.current_rating ?? '—'}${
                s.rating_label ? ` · ${s.rating_label}` : ''
              }\n${s.source_label || '—'}`}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexWrap: 'wrap',
                  }}
                >
                  {s.skill_name}
                  {s.is_stale && <StaleBadge />}
                </div>
                <div
                  style={{
                    color: 'var(--color-dark-grey)',
                    fontSize: '11.5px',
                    marginTop: '1px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subtext}
                </div>
              </div>
              {/*
                Feature #312 — the pip meter carries the rubric definition.
                A `title` on this inner element wins over the row's `title`
                while the cursor is inside it, so hovering the pips shows the
                definition and hovering the name or subtext still shows the
                provenance. Deliberate, and covered by the work item's tests.
                Left aria-hidden: the adjacent `{n} · {label}` text already
                announces the rating, and a native tooltip is a mouse
                affordance regardless.
              */}
              <div
                style={{ display: 'flex', gap: '3px' }}
                aria-hidden="true"
                title={levelDefinition(s.current_rating)}
              >
                {Array.from({ length: MAX_LEVEL }, (_, i) => (
                  <span
                    key={`${s.skill_id}-pip-${i + 1}`}
                    style={{
                      height: '14px',
                      flex: 1,
                      borderRadius: '3px',
                      backgroundColor:
                        i + 1 <= level
                          ? `var(--sm-level-${level})`
                          : 'var(--color-light-grey)',
                    }}
                  />
                ))}
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: 'var(--color-dark-grey)',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {s.current_rating ?? '—'}
                {s.rating_label ? ` · ${s.rating_label}` : ''}
              </div>
            </div>
          )
        })}
      </div>

      {sorted.length > TOP_N && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          style={{
            width: '100%',
            marginTop: '14px',
            background: 'transparent',
            border: '1px dashed var(--color-light-grey)',
            color: 'var(--color-dark-grey)',
            borderRadius: '8px',
            padding: '10px',
            fontFamily: 'var(--font-family)',
            fontSize: '13px',
            cursor: 'pointer',
          }}
        >
          {showAll
            ? `Show top ${TOP_N} only`
            : `Show all ${sorted.length} skills (${sorted.length - TOP_N} more)`}
        </button>
      )}

      {technicalLevels.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '14px',
            alignItems: 'center',
            fontSize: '12px',
            color: 'var(--color-dark-grey)',
            marginTop: '14px',
          }}
        >
          <span>Level</span>
          {technicalLevels.map((r) => (
            <span
              key={r.id}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '3px',
                  backgroundColor: `var(--sm-level-${r.value})`,
                }}
              />
              {r.value} {r.label}
            </span>
          ))}
        </div>
      )}
    </section>
  )
}

export default StrengthSpine
